import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { regulationList, updateRegulationList } from 'actions/Regulations';
import * as taskTemplateStyle from './tasktemplates/TaskTemplateStyle';

function IntegratedPlantProtectionJournal(props) {
  const {
    open,
    onClose,
    intl,
    userDetails,
    userRegulations,
    updateUserRegulations,
    getUserRegulations,
    onRegulationsCheckboxChangeFromModal,
  } = props;

  const [journalEntries, setJournalEntries] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [regulationStructure, setRegulationStructure] = useState([]);
  const selectedColor = '#00696D';

  useEffect(() => {
    if (open && userDetails?.id) {
      getUserRegulations(userDetails.id);
      setIsInitialized(false);
    }
  }, [open, userDetails, getUserRegulations]);

  useEffect(() => {
    if (userRegulations?.regulationList && !isInitialized) {
      const processedData = processRegulationData(userRegulations.regulationList);
      setRegulationStructure(processedData);
      const initialEntries = processedData.reduce((acc, section) => {
        return acc.concat(
          section.items.map((item) => ({
            ...item,
            opted: item.opted || false,
          }))
        );
      }, []);
      setJournalEntries(initialEntries);
      setIsInitialized(true);
    }
  }, [userRegulations, isInitialized]);

  const journalEntriesMap = journalEntries.reduce((acc, entry) => {
    acc[entry.id] = entry;
    return acc;
  }, {});

  const handleCheckboxChange = (id) => {
    setJournalEntries(prevEntries =>
      prevEntries.map(entry =>
        entry.id === id ? { ...entry, opted: !entry.opted } : entry
      )
    );
  };

  const handleSave = async () => {
    const atLeastOneSelected = journalEntries.some(entry => entry.opted);
    const allSelected = journalEntries.every(entry => entry.opted);
    const regulationArray = journalEntries.map((entry) => ({
      regulationId: entry.id,
      opted: entry.opted,
    }));

    const data = {
      regulations: regulationArray,
      userId: userDetails.id,
    };

    await updateUserRegulations(data);
    await getUserRegulations(userDetails.id);

    onRegulationsCheckboxChangeFromModal(atLeastOneSelected, allSelected);
    onClose();
  };

  useEffect(() => {
    if (!isInitialized) return;
    const selectedCount = journalEntries.filter(entry => entry.opted).length;
    const totalCount = journalEntries.length;
    const atLeastOneSelected = selectedCount > 0;
    const allSelected = selectedCount === totalCount;

    onRegulationsCheckboxChangeFromModal(atLeastOneSelected, allSelected);
  }, [journalEntries, isInitialized, onRegulationsCheckboxChangeFromModal]);

  const processRegulationData = (backendData) => {
    const sortedData = [...backendData].sort((a, b) => a.id - b.id);
    const principles = sortedData.slice(0, 8);
    const measures = sortedData.slice(8);
    return [
      {
        header: "Prinsipp",
        items: principles.map(item => ({
          id: item.id,
          regulationName: item.regulationName,
          opted: item.opted,
        })),
      },
      {
        header: "Tiltak for å hindre forurensning",
        items: measures.map(item => ({
          id: item.id,
          regulationName: item.regulationName,
          opted: item.opted,
        })),
      },
    ];
  };

  if (!regulationStructure.length) return null;

  return (
    <div style={{ position: 'relative' }}>
      {open && (
        <div
          style={{
            position: 'relative',
            top: 0,
            left: 0,
            width: '700px',
            maxHeight: '80vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
          }}
        />
      )}
      <Dialog
        onClose={onClose}
        open={open}
        disableScrollLock
        PaperProps={{
          style: {
            backgroundColor: '#E6E9E8',
            borderRadius: '28px',
            width: '700px',
            zIndex: 1001,
            position: 'relative',
          },
        }}
      >
        <DialogTitle sx={{ padding: '24px 24px 0px 24px' }}>
          {intl.formatMessage({ id: "PROFILE_REGULATIONS_LINK" })}*
        </DialogTitle>
        <DialogContent
          style={{
            overflow: 'visible',
            padding: '0px',
          }}
        >
          <Box
            sx={{
              maxHeight: 'calc(70vh - 150px)',
              overflowY: 'auto',
              padding: '0px',
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#A8A8A8',
                borderRadius: '4px',
                transition: 'background-color 0.3s ease',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#B8B8B8',
              },
            }}
          >
            <Typography sx={{ ...taskTemplateStyle.profileRegulationsLinkTextSubHeading, padding: '24px 24px 0px 24px' }}>
              {intl.formatMessage({ id: "SUBHEADING_ONE" })}
            </Typography>
            {regulationStructure[0]?.items?.map((item) => {
              const entry = journalEntriesMap[item.id];
              return entry ? (
                <Box sx={{ padding: '8px 8px 0px 16px' }} key={item.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={entry.opted}
                        onChange={() => handleCheckboxChange(item.id)}
                        sx={{
                          color: entry.opted ? selectedColor : null,
                          '&.Mui-checked': {
                            color: selectedColor,
                          },
                          paddingLeft: '40px',
                        }}
                      />
                    }
                    label={item.regulationName}
                  />
                </Box>
              ) : null;
            })}

            <Typography sx={{ ...taskTemplateStyle.profileRegulationsLinkTextSubHeading, padding: '24px 24px 0px 24px', marginTop: '0px' }} mt={3}>
              {intl.formatMessage({ id: "SUBHEADING_TWO" })}
            </Typography>

            {regulationStructure[1]?.items?.map((item) => {
              const entry = journalEntriesMap[item.id];
              return entry ? (
                <Box sx={{ padding: '8px 8px 0px 16px' }} key={item.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={entry.opted}
                        onChange={() => handleCheckboxChange(item.id)}
                        sx={{
                          color: entry.opted ? selectedColor : null,
                          '&.Mui-checked': {
                            color: selectedColor,
                          },
                          paddingLeft: '40px',
                        }}
                      />
                    }
                    label={item.regulationName}
                  />
                </Box>
              ) : null;
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <Button sx={{ paddingRight: '0px', textTransform: 'none' }} onClick={handleSave}>
            <Typography sx={{
              color: 'var(--M3-sys-light-primary, #00696D)',
              textAlign: 'center',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: '500',
              lineHeight: '20px',
              letterSpacing: '0.1px',
              textTransform: 'none',
            }}>
              {intl.formatMessage({ id: "OMD_OK" })}
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

const mapStateToProps = (state) => ({
  userDetails: state.userDetails.data,
  userRegulations: state.regulations,
});

const mapDispatchToProps = (dispatch) => ({
  getUserRegulations: (userId) => dispatch(regulationList(userId)),
  updateUserRegulations: (data) => dispatch(updateRegulationList(data)),
});

export default injectIntl(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(IntegratedPlantProtectionJournal))
);
